<template>
  <div class="svoi-file-picker">
    <div
      class="drag-area"
      :class="classes"
      @dragover="setActive($event, true)"
      @dragleave="setActive($event, false)"
      @drop="fileDropped"
    >
      <slot name="icon-area">
        <SvoiIcon :icon="icon" size="m" />
      </slot>
      <div class="content">
        <input
          :id="id"
          :accept="accept"
          :multiple="multiple"
          :disabled="disabled"
          :tabindex="tabindex"
          type="file"
          @focus="setActive($event, true)"
          @blur="setActive($event, false)"
          @change="fileAdded"
        />
        <label class="label" :for="id">
          <slot name="label">
            <span :class="{ '-green': !disabled }">Выберите</span>
            {{ label }}
          </slot>
        </label>
        <label v-if="description" class="description">{{ description }}</label>
        <label v-if="complexError" class="error">{{ complexError }}</label>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ComponentPropsOptions, computed, defineComponent, isVue2, onMounted, PropType, ref, watch } from 'vue-demi'
import { SvoiIcon } from '@svoi-ui/components/icon'
import { uuid } from '@svoi-ui/shared/utils/helpers'
import { isRefObject } from '@svoi-ui/shared/utils/guards'
import { BaseFilePickerComponent, useBaseFilePicker } from './BaseFilePicker'

const modelProp: ComponentPropsOptions = isVue2
  ? {
      value: {
        type: Array as PropType<Array<File> | null>,
        default: null
      }
    }
  : {
      modelValue: {
        type: Array as PropType<Array<File> | null>,
        default: null
      }
    }

export default defineComponent({
  name: 'SvoiFilePicker',
  components: { SvoiIcon },
  props: {
    ...BaseFilePickerComponent.props,
    ...modelProp
  },
  emits: ['input', 'update:modelValue'],
  setup(props, { emit }) {
    const { syncElementFiles, checkFiles } = useBaseFilePicker(props)

    const error = ref('')
    const complexError = computed(() => `${props.error} ${error.value}`)
    const isActive = ref(false)

    const id = ref('')
    onMounted(() => (id.value = uuid()))

    const label = computed(() => {
      return props.multiple ? 'файлы или перетащите их сюда' : 'файл или перетащите его сюда'
    })

    const classes = computed(() => ({
      '-active': isRefObject(isActive) && isActive.value,
      '-disabled': props.disabled,
      '-error': isRefObject(complexError) && complexError.value.trim().length > 0
    }))

    const cancelHandlers = (e: Event) => {
      e.preventDefault()
      e.stopPropagation()
    }

    const setActive = (e: DragEvent | FocusEvent, value: boolean) => {
      if (!props.disabled) {
        isActive.value = value
        cancelHandlers(e)
      }
    }

    const fileDropped = (e: DragEvent) => {
      if (props.disabled) {
        return
      }

      setActive(e, false)
      error.value = ''

      let files = Array.from(e.dataTransfer?.files ?? [])

      if (!props.multiple && files.length > 1) {
        error.value = 'Множественная загрузка файлов не поддерживается'
        files = []
      }

      if (files.length > props.maxCount) {
        error.value = `Максимальное количество загруженных файлов не более - ${props.maxCount}`
        files = []
      }

      const checkResult = checkFiles(files)
      if (checkResult !== true) {
        error.value = checkResult
        files = []
      }

      emit(isVue2 ? 'input' : 'update:modelValue', files)
    }

    const fileAdded = (e: Event) => {
      error.value = ''
      let files = Array.from((e.target as HTMLInputElement).files ?? [])

      if (files.length > props.maxCount) {
        error.value = `Максимальное количество загруженных файлов не более - ${props.maxCount}`
        files = []
      }

      const checkResult = checkFiles(files)
      if (checkResult !== true) {
        error.value = checkResult
        files = []
      }

      emit(isVue2 ? 'input' : 'update:modelValue', files)
    }

    watch(
      // @ts-expect-error
      () => (isVue2 ? props.value : props.modelValue),
      () => syncElementFiles(id.value)
    )

    return {
      id,
      complexError,
      isActive,
      label,
      classes,
      fileAdded,
      setActive,
      fileDropped
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@svoi-ui/assets/scss/components/file-picker.scss';
</style>
